// act-as-waap-detail-page.tsx — WAAP service detail page (implicit customer-token context).
// Route: /operations/act-as/waap/:serviceId?tab=<slug>
// Header: Breadcrumbs → h1 + StatusPill (no PageHeader — ops convention).
// All 13 tabs implemented (B1–B4). No ComingSoon stubs remain.
// Guard: EmptyState when no impersonation session is active for any org.
// Anchor: resolves first domain UUID per service for account-level API calls.
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import { DetailHeader } from '@/components/detail-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { SectionLabel } from '@/pages/operations/detail/tabs/ops-detail-shared';
import { getImpersonationSession } from '@/lib/impersonation-session';
import { setImpersonationOrgId } from '@/api/impersonation-client';
import { useActAsWAAPAnchorDomain } from '@/api/act-as-waap';
import { WaapOverviewTab }            from './tabs/waap-overview-tab';
import { WaapWebsitesTab }            from './tabs/waap-websites-tab';
import { WaapLogsTab }                from './tabs/waap-logs-tab';
import { WaapAnalyticsTab }           from './tabs/waap-analytics-tab';
import { WaapAiwafGeneralTab }        from './tabs/waap-aiwaf-general-tab';
import { WaapAiwafSettingsTab }       from './tabs/waap-aiwaf-settings-tab';
import { WaapEmergencyTab }           from './tabs/waap-emergency-tab';
import { WaapProgMitigationTab }      from './tabs/waap-prog-mitigation-tab';
import { WaapAiwafAdvancedTab }       from './tabs/waap-aiwaf-advanced-tab';
import { WaapAiwafComplianceTab }     from './tabs/waap-aiwaf-compliance-tab';
import { WaapAiwafCustomRulesTab }    from './tabs/waap-aiwaf-custom-rules-tab';
import { WaapAiwafWacTab }            from './tabs/waap-aiwaf-wac-tab';
import { WaapBotManagementTab }       from './tabs/waap-bot-management-tab';
import { WaapApiProtectionTab }       from './tabs/waap-api-protection-tab';
import {
  WAAP_TABS,
  WAAP_TAB_LABEL,
  WAAP_TAB_SECTIONS,
  DEFAULT_WAAP_TAB,
  type WaapTab,
} from './waap-domain-tab-registry';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

type PillTone = 'success' | 'warning' | 'danger' | 'muted' | 'neutral';

function waapStatusTone(s: string | undefined): PillTone {
  if (s === 'active')     return 'success';
  if (s === 'pending')    return 'warning';
  if (s === 'error')      return 'danger';
  if (s === 'terminated') return 'muted';
  return 'neutral';
}

/* ── Page ────────────────────────────────────────────────────────────────── */

export default function ActAsWaapDetailPage() {
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  // Guard: bail early when no impersonation session is active for any org.
  const session = getImpersonationSession();
  // Re-activate the org context so impersonation-client sends the customer token
  // on all subsequent API calls (the org detail page cleanup may have cleared it).
  if (session) setImpersonationOrgId(session.orgId);
  if (!session) {
    return (
      <Container size="full">
        <EmptyState
          icon={<ShieldCheck size={32} />}
          title={t('waap_domain.no_session_title', 'Act-as session required')}
          description={t(
            'waap_domain.no_session_description',
            'Activate an act-as session from the service detail page to view WAAP data.',
          )}
        />
      </Container>
    );
  }

  // URL-persisted tab — unknown/missing slugs fall back to overview.
  const rawTab = searchParams.get('tab') ?? '';
  const activeTab: WaapTab = (WAAP_TABS as readonly string[]).includes(rawTab)
    ? (rawTab as WaapTab)
    : DEFAULT_WAAP_TAB;

  const setTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  // Resolve the anchor domain for this service (first domain UUID).
  const anchorQ = useActAsWAAPAnchorDomain(serviceId);

  if (anchorQ.isLoading) {
    return <Container size="full"><TabLoading rows={4} /></Container>;
  }

  if (anchorQ.isError) {
    return (
      <Container size="full">
        <TabError
          title={t('waap_domain.anchor_load_error', 'Failed to resolve WAAP service')}
          onRetry={() => anchorQ.refetch()}
        />
      </Container>
    );
  }

  const anchor = anchorQ.data;
  // If the service has no domains yet, show an informational state rather
  // than crashing — the user may need to add a website first.
  const anchorId = anchor?.id ?? '';
  const serviceName = anchor?.domain_name ?? serviceId;

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* ── Header ────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <DetailHeader
            parent={[
              { label: 'Operations', to: '/operations' },
              { label: 'Organizations', to: '/operations/orgs' },
              { label: 'WAAP', to: `/operations/orgs/${session.orgId}?tab=waap` },
            ]}
            current={serviceName}
            title={serviceName}
            meta={
              anchor?.status ? (
                <StatusPill tone={waapStatusTone(anchor.status)}>{anchor.status}</StatusPill>
              ) : null
            }
          />

          {/* Warn when no anchor domain found — tabs still render but API
              calls return empty; the Websites tab explains why. */}
          {!anchor && (
            <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-subtle)', margin: 0 }}>
              {t(
                'waap_domain.no_anchor_note',
                'No websites found for this WAAP service. Add a website to unlock account-level views.',
              )}
            </p>
          )}
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────── */}
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList style={{ flexWrap: 'wrap' }}>
            {WAAP_TAB_SECTIONS.map(({ label, tabs }) => (
              <span key={label || tabs[0]} style={{ display: 'contents' }}>
                {label && (
                  <SectionLabel style={{ alignSelf: 'center', padding: '0 var(--vn-space-1)' }}>
                    {label}
                  </SectionLabel>
                )}
                {tabs.map((tab) => (
                  <TabsTrigger key={tab} value={tab}>
                    {WAAP_TAB_LABEL[tab]}
                  </TabsTrigger>
                ))}
              </span>
            ))}
          </TabsList>

          {/* ── B1 tabs — fully implemented ─────────────────────────── */}
          <TabsContent value="overview">
            {activeTab === 'overview' && (
              <WaapOverviewTab serviceId={serviceId} anchorId={anchorId} />
            )}
          </TabsContent>

          <TabsContent value="websites">
            {activeTab === 'websites' && <WaapWebsitesTab anchorId={anchorId} />}
          </TabsContent>

          <TabsContent value="logs">
            {activeTab === 'logs' && <WaapLogsTab anchorId={anchorId} />}
          </TabsContent>

          {/* ── B2 — Analytics tab (7 sub-boards, fully implemented) ── */}
          <TabsContent value="analytics">
            {activeTab === 'analytics' && (
              <WaapAnalyticsTab serviceId={serviceId} anchorId={anchorId} />
            )}
          </TabsContent>

          {/* ── B3 tabs — account-level mutation tabs (fully implemented) ── */}
          <TabsContent value="aiwaf-general">
            {activeTab === 'aiwaf-general' && (
              <WaapAiwafGeneralTab anchorId={anchorId} />
            )}
          </TabsContent>

          <TabsContent value="aiwaf-settings">
            {activeTab === 'aiwaf-settings' && (
              <WaapAiwafSettingsTab anchorId={anchorId} />
            )}
          </TabsContent>

          <TabsContent value="emergency">
            {activeTab === 'emergency' && (
              <WaapEmergencyTab serviceId={serviceId} />
            )}
          </TabsContent>

          <TabsContent value="prog-mitigation">
            {activeTab === 'prog-mitigation' && (
              <WaapProgMitigationTab anchorId={anchorId} />
            )}
          </TabsContent>

          {/* ── B4 tabs — fully implemented ─────────────────────────── */}
          <TabsContent value="aiwaf-advanced">
            {activeTab === 'aiwaf-advanced' && (
              <WaapAiwafAdvancedTab anchorId={anchorId} />
            )}
          </TabsContent>

          <TabsContent value="aiwaf-compliance">
            {activeTab === 'aiwaf-compliance' && (
              <WaapAiwafComplianceTab anchorId={anchorId} />
            )}
          </TabsContent>

          <TabsContent value="aiwaf-custom-rules">
            {activeTab === 'aiwaf-custom-rules' && (
              <WaapAiwafCustomRulesTab serviceId={serviceId} />
            )}
          </TabsContent>

          <TabsContent value="aiwaf-wac">
            {activeTab === 'aiwaf-wac' && (
              <WaapAiwafWacTab anchorId={anchorId} />
            )}
          </TabsContent>

          <TabsContent value="bot-management">
            {activeTab === 'bot-management' && (
              <WaapBotManagementTab serviceId={serviceId} />
            )}
          </TabsContent>

          <TabsContent value="api-protection">
            {activeTab === 'api-protection' && (
              <WaapApiProtectionTab anchorId={anchorId} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Container>
  );
}
